import type { UploadEntry } from "@components/FileUpload/uploadStore";
import { VNominationDocumentTypes, type VNominationDocumentType } from "@constants/certificates";
import { FORM_FIELDS } from "@features/vessel-nomination/add/constants";
import type { VesselNominationFormData } from "@features/vessel-nomination/add/schema/vesselNominationFormSchema";
import {
  FORM_FIELDS as PRE_NOMINATION_FORM_FIELDS,
  PRE_NOMINATION_REQUEST_UPLOAD_STORE_KEY,
  type PreNominationRequestFormData,
} from "@features/vessel-nomination/pre-nomination-request/constants";
import type { VesselNominationDetailsResponse } from "@services/vessel-nomination/types";
import { toDate } from "@utils/date-time";

type NominationDocumentsFormData = VesselNominationFormData[typeof FORM_FIELDS.DOCUMENTS];
type UploadStoreWriter = {
  clearFilesForKey: (key: string) => void;
  setFilesForKey: (key: string, files: UploadEntry[]) => void;
};

const NOMINATION_DOCUMENT_TYPES = Object.values(
  VNominationDocumentTypes,
) as VNominationDocumentType[];
const NOMINATION_DOCUMENT_TYPE_SET = new Set<string>(NOMINATION_DOCUMENT_TYPES);
const ADDITIONAL_DOCUMENT_SLOT = "ADDITIONAL_DOCUMENT";
const NONE_VALUE = "NONE";

const buildEmptyNominationDocuments = (): NominationDocumentsFormData =>
  NOMINATION_DOCUMENT_TYPES.reduce((acc, documentType) => {
    acc[documentType] = {
      url: "",
      documentName: "",
      type: "",
      otherSpecify: "",
      issueDate: null,
      expiryDate: null,
      norTime: null,
    };

    return acc;
  }, {} as NominationDocumentsFormData);

const mapOptionalBoolean = (value?: boolean) => value ?? "na";

const hasConditionalText = (value?: string) =>
  value === undefined || value == null ? "" : Boolean(value && value !== NONE_VALUE);

const mapConditionalText = (value?: string) => (value && value !== NONE_VALUE ? value : "");

const mapNominationDocuments = (
  documents: VesselNominationDetailsResponse["documents"] = [],
): NominationDocumentsFormData => {
  const formDocuments = buildEmptyNominationDocuments();

  documents.forEach((document) => {
    const slot = document.slot as VNominationDocumentType;

    if (!NOMINATION_DOCUMENT_TYPE_SET.has(slot)) return;

    formDocuments[slot] = {
      url: document.fileUrl ?? "",
      documentName: document.documentName ?? "",
      type: document.documentType ?? "",
      otherSpecify: document.otherSpecify ?? "",
      issueDate: toDate(document.issueDate),
      expiryDate: toDate(document.expiryDate),
      norTime: toDate(document.norTime),
    };
  });

  return formDocuments;
};

const mapAdditionalNominationDocuments = (
  documents: VesselNominationDetailsResponse["documents"] = [],
): VesselNominationFormData[typeof FORM_FIELDS.ADDITIONAL_NOMINATION_DOCUMENTS] =>
  documents
    .filter(
      (document) =>
        document.slot === ADDITIONAL_DOCUMENT_SLOT ||
        !document.slot ||
        !NOMINATION_DOCUMENT_TYPE_SET.has(document.slot),
    )
    .map((document) => ({
      documentUrl: document.fileUrl ?? "",
      documentName: document.documentName ?? "",
      documentType: document.documentType ?? "",
      otherSpecify: document.otherSpecify ?? "",
      issueDate: toDate(document.issueDate),
      expiryDate: toDate(document.expiryDate),
    }));

export const clearNominationUploadFiles = (uploadStore: UploadStoreWriter) => {
  uploadStore.clearFilesForKey(FORM_FIELDS.PAQ_DOCUMENT);
  NOMINATION_DOCUMENT_TYPES.forEach((documentType) => {
    uploadStore.clearFilesForKey(`vessel-nomination-${documentType}`);
  });
  uploadStore.clearFilesForKey(FORM_FIELDS.ADDITIONAL_NOMINATION_DOCUMENTS);
};

export const hydratePreNominationUploadFiles = (
  nominationDetails: VesselNominationDetailsResponse,
  uploadStore: UploadStoreWriter,
) => {
  const uploadEntries = nominationDetails.documents
    .filter((document) => document.fileUrl)
    .map((document) => ({
      name: document.documentName || "document",
      url: document.fileUrl,
    }));

  uploadStore.setFilesForKey(PRE_NOMINATION_REQUEST_UPLOAD_STORE_KEY, uploadEntries);
};

export const hydrateNominationUploadFiles = (
  nominationDetails: VesselNominationDetailsResponse,
  uploadStore: UploadStoreWriter,
) => {
  if (nominationDetails.paqDocument?.fileUrl) {
    uploadStore.setFilesForKey(FORM_FIELDS.PAQ_DOCUMENT, [
      {
        name: nominationDetails.paqDocument.documentName || FORM_FIELDS.PAQ_DOCUMENT,
        url: nominationDetails.paqDocument.fileUrl,
      },
    ]);
  }

  const additionalDocumentEntries: UploadEntry[] = [];

  nominationDetails.documents?.forEach((document) => {
    if (!document.fileUrl) return;

    const slot = document.slot;
    const uploadEntry: UploadEntry = {
      name: document.documentName || slot || "document",
      url: document.fileUrl,
    };

    if (slot && NOMINATION_DOCUMENT_TYPE_SET.has(slot)) {
      uploadStore.setFilesForKey(`vessel-nomination-${slot}`, [uploadEntry]);
      return;
    }

    additionalDocumentEntries.push(uploadEntry);
  });

  if (additionalDocumentEntries.length) {
    uploadStore.setFilesForKey(
      FORM_FIELDS.ADDITIONAL_NOMINATION_DOCUMENTS,
      additionalDocumentEntries,
    );
  }
};

export const mapNominationDetailsToFormData = (
  nominationDetails: VesselNominationDetailsResponse,
): VesselNominationFormData => {
  const technicalDetails = nominationDetails.technicalDetails ?? {};
  const manifolds = nominationDetails.manifolds ?? {};
  const inspectionDetails = nominationDetails.inspectionDetails ?? {};

  return {
    [FORM_FIELDS.PENDING_UPLOADS]: 0,
    [FORM_FIELDS.VESSEL_ID]: nominationDetails.vesselId,
    [FORM_FIELDS.RTA]: toDate(nominationDetails.rta),
    [FORM_FIELDS.ETA]: toDate(nominationDetails.eta),
    [FORM_FIELDS.CALL_SIGN]: nominationDetails.callSign ?? "",
    [FORM_FIELDS.PREVIOUS_VESSEL_NAME]: nominationDetails.previousVesselName ?? "",
    [FORM_FIELDS.LOA]: technicalDetails.loa,
    [FORM_FIELDS.BALLAST]: technicalDetails.ballast,
    [FORM_FIELDS.DRAUGHT_SUMMER]: technicalDetails.draughtSummer,
    [FORM_FIELDS.DRAUGHT_TROPICAL]: technicalDetails.draughtTropical,
    [FORM_FIELDS.ARRIVAL_FWD]: nominationDetails.arrivalDraught?.fwd,
    [FORM_FIELDS.ARRIVAL_MID]: nominationDetails.arrivalDraught?.mid,
    [FORM_FIELDS.ARRIVAL_AFT]: nominationDetails.arrivalDraught?.aft,
    [FORM_FIELDS.MAX_TRIM]: nominationDetails.arrivalDraught?.maxTrimExpectedDuringOperations,
    [FORM_FIELDS.DEPARTURE_FWD]: nominationDetails.departureDraught?.fwd,
    [FORM_FIELDS.DEPARTURE_MID]: nominationDetails.departureDraught?.mid,
    [FORM_FIELDS.DEPARTURE_AFT]: nominationDetails.departureDraught?.aft,
    [FORM_FIELDS.ARRIVAL_DRAFT]: nominationDetails.arrivalDraft,
    [FORM_FIELDS.DEPARTURE_DRAFT]: nominationDetails.departureDraft,
    [FORM_FIELDS.NET_TONNAGE]: nominationDetails.netTonnage,
    [FORM_FIELDS.GROSS_TONNAGE]: nominationDetails.grossTonnage,
    [FORM_FIELDS.LIGHT]: nominationDetails.displacement?.light,
    [FORM_FIELDS.LOADED]: nominationDetails.displacement?.loaded,
    [FORM_FIELDS.ARRIVAL]: nominationDetails.displacement?.arrival,
    [FORM_FIELDS.DEPARTURE]: nominationDetails.displacement?.departure,
    [FORM_FIELDS.SUMMER_DWT]: nominationDetails.deadWeight?.summerDwt,
    [FORM_FIELDS.TROPICAL_DWT]: nominationDetails.deadWeight?.tropicalDwt,
    [FORM_FIELDS.MANIFOLDS_SIZE]: manifolds.size ?? "",
    [FORM_FIELDS.MANIFOLDS_NUMBER]: manifolds.numberOfManifolds,
    [FORM_FIELDS.MANIFOLDS_DISTANCE]: manifolds.distanceBetweenCenterOfManifolds,
    [FORM_FIELDS.MANIFOLDS_HEIGHT_GRAFTING]: manifolds.manifoldHeightFromGrating,
    [FORM_FIELDS.MANIFOLDS_HEIGHT_BALLAST]: manifolds.manifoldHeightAboveWaterlineBallast,
    [FORM_FIELDS.MANIFOLDS_HEIGHT_LOADED]: manifolds.manifoldHeightAboveWaterlineLoaded,
    [FORM_FIELDS.MANIFOLDS_EXTREME_HEIGHT]:
      manifolds.manifoldExtremeHeightAboveWaterlineDuringOperations,
    [FORM_FIELDS.MANIFOLDS_DISTANCE_BOW_BCM_STERN]: manifolds.distanceBowToManifoldToSternBcm,
    [FORM_FIELDS.MANIFOLDS_DISTANCE_BOW_SCM_STERN]: manifolds.distanceBowToManifoldToSternScm,
    [FORM_FIELDS.MANIFOLDS_DISTANCE_INBOARD]: manifolds.distanceInboardFromManifoldToShipsSideRail,
    [FORM_FIELDS.TERMINAL_LOADING_ARM_CONNECTION]: manifolds.terminalLoadingArmConnectionSuitable,
    [FORM_FIELDS.EXHAUST_GAS_CLEANING_SYSTEM]: manifolds.exhaustGasCleaningSystemFitted,
    [FORM_FIELDS.EXHAUST_GAS_CLEANING_SYSTEM_TYPE]: manifolds.exhaustGasCleaningSystemType ?? "",
    [FORM_FIELDS.CONFIRM_INERT_GAS_SYSTEM_FITTED]: manifolds.inertGasSystemFitted,
    [FORM_FIELDS.IS_INERT_GAS_SYSTEM_OPERATIONAL]:
      manifolds.cotsInInertConditionAndSystemOperational,
    [FORM_FIELDS.VESSEL_CONDITION]:
      nominationDetails.cargoOnboard?.vesselCondition.conditionCode ?? "",
    [FORM_FIELDS.CARGO_TYPE]: nominationDetails.cargoOnboard?.cargoType?.cargoCode ?? "",
    [FORM_FIELDS.CARGO_TYPE_OTHER]: nominationDetails.cargoOnboard?.cargoTypeOther?.cargoCode ?? "",
    [FORM_FIELDS.CARGO_QUANTITY_M3]: nominationDetails.cargoOnboard?.cargoQuantityM3Gov,
    [FORM_FIELDS.CARGO_QUANTITY]: nominationDetails.cargoOnboard?.cargoQuantityMt,
    [FORM_FIELDS.PARCELS]: nominationDetails.parcels.map((parcel) => ({
      id: parcel.id,
      terminal: parcel.customerTerminal.companyCode,
      operationType: parcel.operationType,
      cargoType: parcel.cargoType.cargoCode,
      cargoQuantityCbm: parcel.cargoQuantityCbm,
      cargoQuantityMt: parcel.cargoQuantityMt,
    })),
    [FORM_FIELDS.FLASH_POINT]: nominationDetails.toxicComponentDetails?.flashPoint,
    [FORM_FIELDS.SULPHUR]: nominationDetails.toxicComponentDetails?.sulphurWtPercent,
    [FORM_FIELDS.MERCAPTAN]: nominationDetails.toxicComponentDetails?.mercaptan,
    [FORM_FIELDS.STOWED_CARGO_TEMPERATURE]:
      nominationDetails.toxicComponentDetails?.stowedCargoTemperature,
    [FORM_FIELDS.HYDROGEN_SULPHIDE]: nominationDetails.toxicComponentDetails?.hydrogenSulphidePpm,
    [FORM_FIELDS.RVP]: nominationDetails.toxicComponentDetails?.rvp,
    [FORM_FIELDS.COLOUR_ASTM]: nominationDetails.toxicComponentDetails?.colourAstm ?? "",
    [FORM_FIELDS.BENZENE_CONTENT]:
      nominationDetails.toxicComponentDetails?.benzeneContentVolPercent,
    [FORM_FIELDS.ARE_HIGH_H2S_TANKS_ADEQUATE]: mapOptionalBoolean(
      nominationDetails.toxicComponentDetails?.highH2sTanksPurged,
    ),
    [FORM_FIELDS.AVG_PRESSURE_CARGO_TANKS_ARRIVAL]:
      nominationDetails.toxicComponentDetails?.avgPressureOfCargoTanksOnArrival,
    [FORM_FIELDS.MAX_PRESSURE_MANIFOLDS]:
      nominationDetails.toxicComponentDetails?.maximumAcceptablePressureAtShipsManifolds,
    [FORM_FIELDS.IS_CRUDE_OIL_WASHING_OPERATION_PLANNED]: mapOptionalBoolean(
      nominationDetails.toxicComponentDetails?.crudeOilWashingPlanned,
    ),
    [FORM_FIELDS.MOORING_FWD]: nominationDetails.mooringDetails?.fwd,
    [FORM_FIELDS.MOORING_MID]: nominationDetails.mooringDetails?.mid,
    [FORM_FIELDS.MOORING_AFT]: nominationDetails.mooringDetails?.aft,
    [FORM_FIELDS.HAS_MOORING_DETAILS_AND_DEFICIENCIES]: hasConditionalText(
      nominationDetails.mooringDetails?.mooringArrangementDeficiencies,
    ),
    [FORM_FIELDS.MOORING_DETAILS_AND_DEFICIENCIES]: mapConditionalText(
      nominationDetails.mooringDetails?.mooringArrangementDeficiencies,
    ),
    [FORM_FIELDS.HAS_ANY_DEFECTS_HULL_MACHINERY_EQUIPMENT]: hasConditionalText(
      nominationDetails.mooringDetails?.defectsAffectingSafeOperations,
    ),
    [FORM_FIELDS.ANY_DEFECTS_HULL_MACHINERY_EQUIPMENT]: mapConditionalText(
      nominationDetails.mooringDetails?.defectsAffectingSafeOperations,
    ),
    [FORM_FIELDS.HAS_AREA_WITH_PROTRUSION]:
      hasConditionalText(nominationDetails.mooringDetails?.hullProtrusionArea) ||
      nominationDetails.hasHullProtrusion,
    [FORM_FIELDS.AREA_WITH_PROTRUSION]: mapConditionalText(
      nominationDetails.mooringDetails?.hullProtrusionArea,
    ),
    [FORM_FIELDS.LAST_SIRE_INSPECTION]: toDate(inspectionDetails.lastSireInspectionDate ?? ""),
    [FORM_FIELDS.ISSUING_AUTHORITY]: inspectionDetails.lastSireInspectionIssuingAuthority ?? "",
    [FORM_FIELDS.LAST_PSC_INSPECTION]: toDate(inspectionDetails.lastPscInspectionDate ?? ""),
    [FORM_FIELDS.PLACE]: inspectionDetails.lastPscInspectionPlace ?? "",
    [FORM_FIELDS.LAST_PORT]:
      nominationDetails.voyageInformation?.lastPortOfCallCode?.portCode ?? "",
    [FORM_FIELDS.NEXT_PORT]:
      nominationDetails.voyageInformation?.nextPortOfCallCode?.portCode ?? "",
    [FORM_FIELDS.SECURITY_LEVEL]: nominationDetails.voyageInformation?.securityLevel ?? "",
    [FORM_FIELDS.PAQ_DOCUMENT]: {
      url: nominationDetails.paqDocument?.fileUrl ?? "",
      documentName: nominationDetails.paqDocument?.documentName ?? "",
    },
    [FORM_FIELDS.CONFIRMATION]: nominationDetails.acknowledged ?? false,
    [FORM_FIELDS.DOCUMENTS]: mapNominationDocuments(nominationDetails.documents),
    [FORM_FIELDS.REMARKS]: nominationDetails.remarks ?? "",
    [FORM_FIELDS.ADDITIONAL_NOMINATION_DOCUMENTS]: mapAdditionalNominationDocuments(
      nominationDetails.documents,
    ),
  };
};

export const mapPreNominationDetailsToFormData = (
  nominationDetails: VesselNominationDetailsResponse,
): PreNominationRequestFormData => {
  return {
    [PRE_NOMINATION_FORM_FIELDS.VESSEL_ID]: nominationDetails.vesselId,
    [PRE_NOMINATION_FORM_FIELDS.ETA]: toDate(nominationDetails.eta),
    [PRE_NOMINATION_FORM_FIELDS.CALL_SIGN]: nominationDetails.callSign ?? "",
    [PRE_NOMINATION_FORM_FIELDS.SUMMER_DWT]: nominationDetails.deadWeight?.summerDwt,
    [PRE_NOMINATION_FORM_FIELDS.ARRIVAL_DRAFT]: nominationDetails.arrivalDraft,
    [PRE_NOMINATION_FORM_FIELDS.DEPARTURE_DRAFT]: nominationDetails.departureDraft,
    [PRE_NOMINATION_FORM_FIELDS.HAS_HULL_PROTRUSION]: nominationDetails.hasHullProtrusion,
    [PRE_NOMINATION_FORM_FIELDS.PARCELS]: nominationDetails.parcels.map((parcel) => ({
      id: parcel.id,
      terminal: parcel.customerTerminal.companyCode,
      operationType: parcel.operationType,
      cargoType: parcel.cargoType.cargoCode,
      cargoQuantityCbm: parcel.cargoQuantityCbm,
      cargoQuantityMt: parcel.cargoQuantityMt,
    })),
    [PRE_NOMINATION_FORM_FIELDS.REMARKS]: nominationDetails.remarks ?? "",
    [PRE_NOMINATION_FORM_FIELDS.ATTACHMENTS]: nominationDetails.documents.map((document) => ({
      documentUrl: document.fileUrl ?? "",
      documentName: document.documentName ?? "",
      documentType: document.documentType ?? "",
      otherSpecify: document.otherSpecify ?? "",
      issueDate: toDate(document.issueDate),
      expirationDate: toDate(document.expiryDate),
      documentSummary: document.documentSummary ?? "",
    })),
    [PRE_NOMINATION_FORM_FIELDS.CONFIRMATION]: false,
  };
};
